import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMail, FiCheck, FiAlertCircle, FiLoader, FiRefreshCw } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { sendTestEmail, listCountries } from '../../services/billing';
import CountryManager from './CountryManager';

export default function AdminSettings() {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const [countries, setCountries] = useState([]);

  const loadCountries = async () => {
    try {
      setCountries(await listCountries());
    } catch (err) {
      console.error('Failed to load countries:', err);
    }
  };

  useEffect(() => {
    loadCountries();
  }, []);

  const handleTest = async () => {
    setTesting(true);
    setResult(null);
    try {
      await sendTestEmail();
      setResult({ success: true, message: 'Test email sent successfully! Check your inbox at admin@hostfig.com' });
    } catch (err) {
      setResult({ success: false, message: err.message || 'Failed to send test email' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Email Configuration */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm"
      >
        <div className="flex items-start gap-4 mb-6">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg shrink-0">
            <SafeIcon icon={FiMail} className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">Email Reminders</h3>
            <p className="text-sm text-slate-500 mt-1">
              Test your SMTP configuration by sending a test email to your admin address.
              Reminder emails are sent automatically by the scheduled edge function.
            </p>
          </div>
        </div>

        <div className="rounded-xl bg-slate-50 border border-slate-100 p-4 mb-6">
          <div className="grid gap-3 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">SMTP Host</span>
              <span className="font-medium text-slate-700">smtp.stackmail.com</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Port</span>
              <span className="font-medium text-slate-700">465 (SSL)</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Recipient</span>
              <span className="font-medium text-slate-700">admin@hostfig.com</span>
            </div>
          </div>
        </div>

        <button
          onClick={handleTest}
          disabled={testing}
          className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3 text-sm font-semibold text-white transition hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 shadow-lg shadow-blue-600/20"
        >
          {testing ? (
            <>
              <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
              Sending test email...
            </>
          ) : (
            <>
              <SafeIcon icon={FiMail} className="w-4 h-4" />
              Send Test Email
            </>
          )}
        </button>

        <AnimatePresence>
          {result && (
            <motion.div
              initial={{ opacity: 0, y: -8, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, y: -8, height: 0 }}
              className="overflow-hidden"
            >
              <div className={`mt-4 flex items-start gap-3 rounded-xl p-4 ${result.success ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                <SafeIcon
                  icon={result.success ? FiCheck : FiAlertCircle}
                  className={`w-5 h-5 mt-0.5 shrink-0 ${result.success ? 'text-emerald-500' : 'text-red-500'}`}
                />
                <div>
                  <p className={`text-sm font-semibold ${result.success ? 'text-emerald-800' : 'text-red-800'}`}>
                    {result.success ? 'Success' : 'Failed'}
                  </p>
                  <p className={`text-sm mt-0.5 ${result.success ? 'text-emerald-700' : 'text-red-700'}`}>
                    {result.message}
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Country Manager */}
      <CountryManager countries={countries} onRefresh={loadCountries} />

      {/* Edge Function Info */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm"
      >
        <h3 className="text-lg font-bold text-slate-900 mb-4">Scheduler Setup</h3>
        <p className="text-sm text-slate-500 mb-4">
          To automate daily reminder emails, set up an external cron job (e.g., cron-job.org, GitHub Actions, or UptimeRobot) that sends a{' '}
          <code className="px-1.5 py-0.5 rounded bg-slate-100 text-xs font-mono text-slate-700">POST</code> request daily:
        </p>
        <div className="rounded-xl bg-slate-900 p-4 text-sm font-mono text-slate-300 overflow-x-auto">
          <div className="text-slate-500"># Daily at 9:00 AM SGT</div>
          <div className="mt-1">
            <span className="text-emerald-400">POST</span>{' '}
            <span className="text-blue-400">https://rrgjvtdptwvizfinixhl.supabase.co/functions/v1/send-billing-reminders</span>
          </div>
          <div className="mt-1 text-slate-500">Headers:</div>
          <div className="text-amber-400">x-billing-secret: YOUR_CRON_SECRET</div>
        </div>
        <p className="text-xs text-slate-400 mt-3">
          Replace <code className="font-mono">YOUR_CRON_SECRET</code> with the value you set for{' '}
          <code className="font-mono">BILLING_CRON_SECRET</code> in your Edge Function secrets.
        </p>
      </motion.div>
    </div>
  );
}